/**
 * EffortTimeStepper - Stepper control for effort time
 *
 * Displays a [-] [value] [+] stepper for selecting effort time.
 * Values step through predefined increments (15 min to 1 week).
 */

import { Minus, Plus } from 'lucide-react';
import { clsx } from 'clsx';
import {
  effortHoursToDisplay,
  getEffortStepUp,
  getEffortStepDown,
  EFFORT_STEPS,
} from '@perfin/shared';

interface EffortTimeStepperProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export function EffortTimeStepper({
  value,
  onChange,
  disabled = false,
}: EffortTimeStepperProps) {
  const isAtMin = value <= EFFORT_STEPS[0];
  const isAtMax = value >= EFFORT_STEPS[EFFORT_STEPS.length - 1];

  const handleDecrement = () => {
    if (disabled || isAtMin) return;
    onChange(getEffortStepDown(value));
  };

  const handleIncrement = () => {
    if (disabled || isAtMax) return;
    onChange(getEffortStepUp(value));
  };

  return (
    <div className="flex items-center w-full">
      {/* Minus button */}
      <button
        type="button"
        onClick={handleDecrement}
        disabled={disabled || isAtMin}
        className={clsx(
          'flex items-center justify-center w-9 h-[38px] rounded-l-lg border border-r-0 transition-colors',
          disabled || isAtMin
            ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed'
            : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
        )}
        title="Decrease effort"
      >
        <Minus className="h-4 w-4" />
      </button>

      {/* Value display */}
      <div
        className={clsx(
          'flex items-center justify-center h-[38px] px-1 border-y min-w-[52px] flex-1 text-sm font-medium',
          disabled
            ? 'bg-gray-50 border-gray-200 text-gray-400'
            : 'bg-white border-gray-300 text-gray-700'
        )}
      >
        {effortHoursToDisplay(value)}
      </div>

      {/* Plus button */}
      <button
        type="button"
        onClick={handleIncrement}
        disabled={disabled || isAtMax}
        className={clsx(
          'flex items-center justify-center w-9 h-[38px] rounded-r-lg border border-l-0 transition-colors',
          disabled || isAtMax
            ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed'
            : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
        )}
        title="Increase effort"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
